import {
	type CustomMarketViewSettings,
	type DefaultMarketViewSettings,
	type MarketScenarioMinInfo,
	type MarketViewAssetClassAlias,
	type MarketViewAssetClassAliases,
	MarketViewAssetClassIdentifier,
	MarketViewType,
} from "@eu.mdotm.api/sphere";
import custom from "$root/assets/images/market-view-settings[preload]/custom.png";
import expected from "$root/assets/images/market-view-settings[preload]/expected.png";
import under_over from "$root/assets/images/market-view-settings[preload]/under-over.png";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { PageCtxConsumer } from "$root/components/PageCtx";
import { ReactQueriesWrapperBase, ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { marketViewRepository } from "$root/functional-areas/market-view/repository";
import { platformToast } from "$root/notification-system/toast";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { CommonItemActions } from "$root/ui-lib/interactive-collections/common-item-actions";
import { parallelizeWithAllSettled } from "$root/utils";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	type Option,
	type TableColumn,
	AutoSortTable,
	Banner,
	CircularProgressBar,
	Dialog,
	DialogFooter,
	Icon,
	Row,
	Searchable,
	Text,
	useSelectableTableColumn,
} from "@mdotm/shadcn/legacy/components";
import { TextInput, Checkbox, Button, Form, FormField, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import { useMultiSelect, useSearchable } from "@mdotm/shadcn/legacy/headless";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { useQueries } from "@tanstack/react-query";
import { Map } from "immutable";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Controller as FormController, useForm } from "react-hook-form";
import { type UseTranslationResponse, useTranslation } from "react-i18next";
import { z } from "zod";
import { usePortfolioStudioRedirect } from "$root/pages/PortfoliosStudio/usePortfolioStudioRedirect";

const marketViewTypeOptions = [
	{
		label: "Expected returns",
		value: MarketViewType.ExpectedReturnsVolatility,
	},
	{ label: "Under/over weight", value: MarketViewType.PositioningIndicators },
];

const qualifierMarketViewType = {
	EXPECTED_RETURNS_VOLATILITY: "ExpectedReturns",
	POSITIONING_INDICATORS: "Positioning",
} satisfies Record<MarketViewType, string>;

function MarketViewSetting(props: { active: boolean }): React.JSX.Element {
	const { active } = props;

	const queriesMarketViewSettings = useQueries({
		queries: [
			{
				...marketViewRepository.marketViewSettingsStandardConfiguration.getQueryConfig("EXPECTED_RETURNS_VOLATILITY"),
				enabled: active,
			},
			{
				...marketViewRepository.marketViewSettingsStandardConfiguration.getQueryConfig("POSITIONING_INDICATORS"),
				enabled: active,
			},
			{
				...marketViewRepository.marketViewSettingsCustomConfiguration.getQueryConfig(),
				enabled: active,
			},
			{
				...marketViewRepository.assetClassAliases.getQueryConfig(),
				enabled: active,
			},
		],
	});
	return (
		<ReactQueriesWrapperBase queries={queriesMarketViewSettings}>
			{([expectedReturnConfiguration, positioningConfiguration, customConfiguration, assetClassAliases], queries) => (
				<MarketViewSettingInner
					{...props}
					underOverWeightMarketEnabled={positioningConfiguration.enabled}
					expectedReturnsMarketEnabled={expectedReturnConfiguration.enabled}
					customMarketViewEnabled={customConfiguration.enabled}
					customMarketViewName={customConfiguration.customMarketViewName}
					customMarketViewType={customConfiguration.type ?? "EXPECTED_RETURNS_VOLATILITY"}
					customMarketViewScenarioIds={customConfiguration.customMarketViewScenarios}
					customMarketViewAssetClassIdentifier={customConfiguration.assetClasses}
					expectedReturnsMarketViewAssetClassIdentifier={expectedReturnConfiguration.assetClasses}
					underOverWeightMarketViewAssetClassIdentifier={positioningConfiguration.assetClasses}
					marketViewsAliases={assetClassAliases.assetClassAliases}
					onSubmitEnd={async () => {
						await parallelizeWithAllSettled(queries.map((q) => () => q.refetch().catch(noop)));
					}}
				/>
			)}
		</ReactQueriesWrapperBase>
	);
}

type MarketViewSettingsInnerProps = {
	// activeMarketViewTypes: MarketViewSettingsActiveMarketViewTypesEnum[] | undefined;
	underOverWeightMarketEnabled: boolean | undefined;
	expectedReturnsMarketEnabled: boolean | undefined;
	customMarketViewEnabled: boolean | undefined;
	customMarketViewName: string | undefined;
	customMarketViewType: MarketViewType;
	customMarketViewScenarioIds: MarketScenarioMinInfo[] | undefined;
	customMarketViewAssetClassIdentifier: MarketViewAssetClassIdentifier[] | undefined;
	expectedReturnsMarketViewAssetClassIdentifier: MarketViewAssetClassIdentifier[] | undefined;
	underOverWeightMarketViewAssetClassIdentifier: MarketViewAssetClassIdentifier[] | undefined;
	marketViewsAliases: Array<MarketViewAssetClassAlias> | undefined;
	onSubmitEnd(): MaybePromise<void>;
};

const MarketViewSettingInner = (props: MarketViewSettingsInnerProps & { active: boolean }): React.JSX.Element => {
	const { t } = useTranslation();

	const initialAliases = useMemo(
		() =>
			Map</* microAssetClass */ MarketViewAssetClassIdentifier, /* alias */ string>(
				props.marketViewsAliases?.map((assetClass) => [assetClass.microAssetClass!, assetClass.alias!]),
			),
		[props.marketViewsAliases],
	);

	const { control, setValue, formState, handleSubmit, watch, reset } = useForm({
		defaultValues: {
			underOverWeightMarketEnabled: props.underOverWeightMarketEnabled ?? false,
			expectedReturnsMarketEnabled: props.expectedReturnsMarketEnabled ?? false,
			customMarketViewEnabled: props.customMarketViewEnabled ?? false,
			customMarketViewName: props.customMarketViewName ?? "",
			customMarketViewType: props.customMarketViewType,
			customMarketViewScenarioIds: props.customMarketViewScenarioIds ?? [],
			customMarketViewAssetClassIdentifier: props.customMarketViewAssetClassIdentifier ?? [],
			expectedReturnsMarketViewAssetClassIdentifier: props.expectedReturnsMarketViewAssetClassIdentifier ?? [],
			underOverWeightMarketViewAssetClassIdentifier: props.underOverWeightMarketViewAssetClassIdentifier ?? [],
			aliases: initialAliases,
		},
		resolver: zodResolver(
			z
				.object({
					underOverWeightMarketEnabled: z.boolean(),
					expectedReturnsMarketEnabled: z.boolean(),
					customMarketViewEnabled: z.boolean(),
					customMarketViewName: z.string(),
					customMarketViewType: z.nativeEnum(MarketViewType),
					customMarketViewScenarioIds: z.array(
						z.object({
							customMarketView: z.boolean().optional(),
							forecastHorizon: z.any().optional(),
							id: z.string().optional(),
							label: z.string().optional(),
							marketViewType: z.nativeEnum(MarketViewType).optional(),
							userCustomScenario: z.boolean().optional(),
							editable: z.boolean().optional(),
						} satisfies Record<keyof MarketScenarioMinInfo, unknown>),
					),
					customMarketViewAssetClassIdentifier: z.array(z.nativeEnum(MarketViewAssetClassIdentifier)),
					expectedReturnsMarketViewAssetClassIdentifier: z.array(z.nativeEnum(MarketViewAssetClassIdentifier)),
					underOverWeightMarketViewAssetClassIdentifier: z.array(z.nativeEnum(MarketViewAssetClassIdentifier)),
					aliases: z.any(),
				})
				.superRefine((settings, ctx) => {
					if (!settings.customMarketViewEnabled) {
						return;
					}
					if (!settings.customMarketViewName) {
						ctx.addIssue({
							message: "Insert a valid market view name",
							path: ["customMarketViewName"],
							code: "custom",
						});
					}
					if (settings.customMarketViewScenarioIds.length === 0) {
						ctx.addIssue({
							message: "Select a scenario",
							path: ["customMarketViewScenarioIds"],
							code: "custom",
						});
					}
					if (settings.customMarketViewAssetClassIdentifier.length === 0) {
						ctx.addIssue({
							message: "Select a micro asset class",
							path: ["customMarketViewAssetClassIdentifier"],
							code: "custom",
						});
					}
				}),
		),
	});

	function onSubmit() {
		return handleSubmit(async (data) => {
			if (
				formState.dirtyFields.expectedReturnsMarketViewAssetClassIdentifier ||
				formState.dirtyFields.expectedReturnsMarketEnabled
			) {
				const expectedReturnsWeightPayload = {
					assetClasses: data.expectedReturnsMarketViewAssetClassIdentifier,
					enabled: data.expectedReturnsMarketEnabled,
					type: MarketViewType.ExpectedReturnsVolatility,
				} satisfies DefaultMarketViewSettings;
				await marketViewRepository.editStandardMarketViewSettings(expectedReturnsWeightPayload);
			}

			if (
				formState.dirtyFields.underOverWeightMarketViewAssetClassIdentifier ||
				formState.dirtyFields.underOverWeightMarketEnabled
			) {
				const underOverWeightPayload = {
					assetClasses: data.underOverWeightMarketViewAssetClassIdentifier,
					enabled: data.underOverWeightMarketEnabled,
					type: MarketViewType.PositioningIndicators,
				} satisfies DefaultMarketViewSettings;
				await marketViewRepository.editStandardMarketViewSettings(underOverWeightPayload);
			}

			if (
				formState.dirtyFields.customMarketViewAssetClassIdentifier ||
				formState.dirtyFields.customMarketViewName ||
				formState.dirtyFields.customMarketViewScenarioIds ||
				formState.dirtyFields.customMarketViewEnabled
			) {
				const customViewPayload = {
					assetClasses: data.customMarketViewAssetClassIdentifier,
					customMarketViewName: data.customMarketViewName,
					customMarketViewScenarios: data.customMarketViewScenarioIds,
					enabled: data.customMarketViewEnabled,
					type: data.customMarketViewType,
				} satisfies CustomMarketViewSettings;
				await marketViewRepository.editCustomMarketViewSettings(customViewPayload);
			}

			if (formState.dirtyFields.aliases) {
				const marketViewAssetClassAliasesPayload = {
					assetClassAliases: data.aliases.toArray().map(([microAssetClass, alias]) => ({ microAssetClass, alias })),
				} satisfies MarketViewAssetClassAliases;
				await marketViewRepository.editMarketViewSettingsAliases(marketViewAssetClassAliasesPayload);
			}

			await props.onSubmitEnd();
			reset(data);
			platformToast({
				children: "Settings saved",
				severity: "success",
				icon: "Outline",
			});
		}, console.log)();
	}

	const queryMarketViewSettingsSelectableScenario = marketViewRepository.marketViewSettingsSelectableScenario.useQuery(
		watch("customMarketViewType"),
	);

	const queryMarketViewSettingsSelectableScenarioOptions = useMappedQuery(
		({ marketScenarioIds }): Option<MarketScenarioMinInfo>[] => {
			if (!marketScenarioIds || marketScenarioIds.length === 0) {
				return [];
			}

			return marketScenarioIds
				.filter((scenario) => scenario.id && scenario.label)
				.map((scenario) => ({
					value: scenario,
					label: scenario.label!,
				}));
		},
		[],
		queryMarketViewSettingsSelectableScenario,
	);

	const portfolioStudio = usePortfolioStudioRedirect();
	return (
		<>
			<Form onSubmitAsync={() => onSubmit()}>
				<div className="grid gap-8 mb-6">
					<div className="flex gap-4">
						<div>
							<img src={expected} className="w-[136px] h-auto" alt="expected" />
						</div>
						<div className="grow">
							<div className="mb-4">
								<FormController
									control={control}
									name="expectedReturnsMarketEnabled"
									render={({ field }) => (
										<Checkbox
											data-qualifier={`MarketViewSettings/${qualifierMarketViewType["EXPECTED_RETURNS_VOLATILITY"]}/Enable`}
											checked={field.value}
											onChange={field.onChange}
										>
											<Text type="Body/XL/Bold" className="mt-0.5">
												Exp. returns market view settings
											</Text>
										</Checkbox>
									)}
								/>
							</div>
							<div className="space-y-4 pl-6">
								<div className="w-full">
									<MicroAssetClassTable
										aliases={watch("aliases")}
										onBatchRename={(newAliases) => setValue("aliases", newAliases, { shouldDirty: true })}
										onSelectionChange={(newMicro) =>
											setValue("expectedReturnsMarketViewAssetClassIdentifier", newMicro, { shouldDirty: true })
										}
										rows={watch("expectedReturnsMarketViewAssetClassIdentifier")}
										assetClassType="EXPECTED_RETURNS_VOLATILITY"
									/>
								</div>
							</div>
						</div>
					</div>
					<hr className="h-px" style={{ backgroundColor: themeCSSVars.palette_N300 }} />
					<div className="flex gap-4">
						<div>
							<img src={under_over} className="w-[136px] h-auto" alt="under-over" />
						</div>
						<div className="grow">
							<div className="mb-4">
								<FormController
									control={control}
									name="underOverWeightMarketEnabled"
									render={({ field }) => (
										<Checkbox
											data-qualifier={`MarketViewSettings/${qualifierMarketViewType["POSITIONING_INDICATORS"]}/Enable`}
											checked={field.value}
											onChange={field.onChange}
										>
											<Text type="Body/XL/Bold" className="mt-0.5">
												Under/Over weight market view settings
											</Text>
										</Checkbox>
									)}
								/>
							</div>
							<div className="space-y-4 pl-6">
								<div className="w-full">
									<MicroAssetClassTable
										aliases={watch("aliases")}
										onBatchRename={(newAliases) => setValue("aliases", newAliases, { shouldDirty: true })}
										onSelectionChange={(newMicro) =>
											setValue("underOverWeightMarketViewAssetClassIdentifier", newMicro, { shouldDirty: true })
										}
										rows={watch("underOverWeightMarketViewAssetClassIdentifier")}
										assetClassType="POSITIONING_INDICATORS"
									/>
								</div>
							</div>
						</div>
					</div>
					<hr className="h-px" style={{ backgroundColor: themeCSSVars.palette_N300 }} />
					<div className="flex gap-4">
						<div>
							<img src={custom} className="w-[136px] h-auto" alt="custom" />
						</div>
						<div className="grow">
							<div className="mb-4">
								<FormController
									control={control}
									name="customMarketViewEnabled"
									render={({ field }) => (
										<Checkbox
											data-qualifier="MarketViewSettings/Custom/Enable"
											checked={field.value}
											onChange={field.onChange}
										>
											<Text type="Body/XL/Bold" className="mt-0.5">
												Custom market view settings
											</Text>
										</Checkbox>
									)}
								/>
							</div>
							<div className="space-y-4 pl-6">
								<div className="flex flex-col space-y-4 max-w-[336px]">
									<FormFields.Text
										formState={formState}
										control={control}
										label="Custom name"
										name="customMarketViewName"
										data-qualifier="MarketViewSettings/CustomMarketView/Field(Name)"
									/>
									<FormFields.Select
										formState={formState}
										control={control}
										label="Market view type"
										name="customMarketViewType"
										multi={false}
										options={marketViewTypeOptions}
										placeholder="Market view type"
										onChange={(marketViewType: MarketViewType) => {
											setValue("customMarketViewType", marketViewType);
											setValue("customMarketViewAssetClassIdentifier", [], { shouldDirty: true });
											setValue("customMarketViewScenarioIds", [], { shouldDirty: true });
										}}
										triggerDataAttrs={{ "data-qualifier": "MarketViewSettings/CustomMarketView/Field(Type)" }}
									/>
									<div>
										<ReactQueryWrapperBase query={queryMarketViewSettingsSelectableScenarioOptions}>
											{(options) => (
												<FormFields.Select
													formState={formState}
													control={control}
													label="Market view scenarios"
													name="customMarketViewScenarioIds"
													options={options}
													placeholder="Market view scenarios"
													multi
													triggerDataAttrs={{
														"data-qualifier": "MarketViewSettings/CustomMarketView/Field(Scenarios)",
													}}
												/>
											)}
										</ReactQueryWrapperBase>
										<p className={`text-[var(--palette-N200)]`}>Choose as many horizons as you want to be available</p>
									</div>
								</div>
								<div className="w-full">
									<MicroAssetClassTable
										aliases={watch("aliases")}
										onBatchRename={(newAliases) => setValue("aliases", newAliases, { shouldDirty: true })}
										onSelectionChange={(newMicro) =>
											setValue("customMarketViewAssetClassIdentifier", newMicro, { shouldDirty: true })
										}
										rows={watch("customMarketViewAssetClassIdentifier")}
										assetClassType={watch("customMarketViewType")}
										custom
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
				{props.active && (
					<PageCtxConsumer>
						{(ctx) =>
							ctx.titleActionPortalTarget &&
							createPortal(
								<div className="flex items-center gap-2">
									<Button size="small" palette="tertiary" onClick={() => portfolioStudio.navigate()}>
										Cancel
									</Button>
									<Button size="small" palette="primary" onClickAsync={onSubmit}>
										Save
									</Button>
								</div>,
								ctx.titleActionPortalTarget,
							)
						}
					</PageCtxConsumer>
				)}
			</Form>
			<LeavePrompt
				title={t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.TITLE")}
				when={formState.isDirty}
				pathToNotBlock={["/portfolio_studio_settings?tab=Market+view+settings"]}
				showCancelIcon={false}
			>
				{t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.MESSAGE")}
			</LeavePrompt>
		</>
	);
};

const RenameMicroAssetClassDialog = (props: {
	show: boolean;
	onSubmitAsync(
		aliases: Map</* microAssetClass */ MarketViewAssetClassIdentifier, /* alias */ string>,
	): MaybePromise<void>;
	aliases: Map</* microAssetClass */ MarketViewAssetClassIdentifier, /* alias */ string>;
	onClose(): void;
	rows: Array<MarketViewAssetClassIdentifier>;
}) => {
	const { t } = useTranslation();

	const [aliases, setAliases] = useState(props.aliases);

	useEffect(() => {
		if (props.show) {
			setAliases(props.aliases); // male resync alias on open
		}
	}, [props.aliases, props.show]);

	const {
		query: searchedMicroAssetClasses,
		setQuery: setSearchedMicroAssetClasses,
		filtered,
	} = useSearchable({
		collection: props.rows,
		extractFn: (item) => `${t(`MARKET_STUDIO_SETTINGS.${item}` as any)} ${aliases.get(item) ?? ""}`,
	});

	const columns = useMemo<Array<TableColumn<MarketViewAssetClassIdentifier>>>(
		() => [
			microAssetClassColumns.microAssetClass(t),
			{
				header: t("TABLE.HEADERS.ALIAS"),
				minWidth: 220,
				name: "alias",
				sortFn: (a, b) => ((aliases.get(a) ?? "") > (aliases.get(b) ?? "") ? -1 : 1),
				content: (microAssetClass, cellProps) => (
					<Row justifyContent="center" {...cellProps} childrenGrow={1}>
						<TextInput
							className="grow min-w-0"
							value={aliases.get(microAssetClass) ?? ""}
							onChangeText={(alias) => setAliases(aliases.set(microAssetClass, alias))}
							data-qualifier={`MarketViewSettings/Dialog(Rename)/Column(alias)/${t(
								`MARKET_STUDIO_SETTINGS.${microAssetClass}`,
							)}`}
						/>
					</Row>
				),
			},
		],
		[aliases, t],
	);

	return (
		<Dialog
			show={props.show}
			header="Rename asset classes"
			size="xxlarge"
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton data-qualifier="MarketViewSettings/Dialog(Rename)/Action(Done)">
							{t("BUTTON.DONE")}
						</SubmitButton>
					}
					neutralAction={
						<Button
							data-qualifier="MarketViewSettings/Dialog(Rename)/Action(Submit)"
							palette="tertiary"
							onClick={props.onClose}
						>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
			onSubmitAsync={() => props.onSubmitAsync(aliases)}
			onClose={props.onClose}
		>
			<Banner severity="info" className="mb-4">
				The customised names will be used in all market views
			</Banner>
			<TextInput
				leftContent={<Icon icon="Search" />}
				value={searchedMicroAssetClasses}
				onChangeText={setSearchedMicroAssetClasses}
				placeholder="Search"
				className="mb-4"
				data-qualifier="MarketViewSettings/Dialog(Rename)/Search"
			/>

			<AutoSortTable style={{ maxHeight: 420 }} rows={filtered} columns={columns} />
		</Dialog>
	);
};

export default MarketViewSetting;

const microAssetClassColumns = {
	microAssetClass(t: UseTranslationResponse<"en", undefined>["0"]): TableColumn<MarketViewAssetClassIdentifier> {
		return {
			header: "Micro Asset Class",
			name: "microAssetClass",
			sortFn: (a, b) => (a > b ? 1 : -1),
			content: (microAssetClass) => t(`MARKET_STUDIO_SETTINGS.${microAssetClass}`),
			minWidth: 220,
		};
	},
};

function MicroAssetClassTable({
	rows,
	aliases,
	onSelectionChange,
	onBatchRename,
	assetClassType,
	custom,
}: {
	rows: MarketViewAssetClassIdentifier[];
	onSelectionChange(microAssetClasses: MarketViewAssetClassIdentifier[]): void;
	aliases: Map</* microAssetClass */ MarketViewAssetClassIdentifier, /* alias */ string>;
	onBatchRename(aliases: Map</* microAssetClass */ MarketViewAssetClassIdentifier, /* alias */ string>): void;
	assetClassType: MarketViewType;
	custom?: boolean;
}) {
	const { t } = useTranslation();
	const rowsRef = useUnsafeUpdatedRef(rows);
	const assetClassAliasTableColumns = useMemo<Array<TableColumn<MarketViewAssetClassIdentifier>>>(
		() => [
			microAssetClassColumns.microAssetClass(t),
			{
				header: "Custom Name",
				name: "alias",
				sortFn: (a, b) => ((aliases.get(a) ?? "") > (aliases.get(b) ?? "") ? -1 : 1),
				minWidth: 220,
				content: (microAssetClass) => aliases.get(microAssetClass) ?? "",
			},
			{
				name: "actions",
				header: null,
				width: 64,
				content: (microAssetClass, cellProps) => (
					<Row {...cellProps} alignItems="center" justifyContent="center">
						<CommonItemActions.Delete
							onDelete={() =>
								onSelectionChange(
									rowsRef.current.filter((refMicroAssetClass) => refMicroAssetClass !== microAssetClass),
								)
							}
							data-qualifier={`MarketViewSettings/${
								custom ? "Custom" : qualifierMarketViewType[assetClassType]
							}/Column(Actions)/Delete(${t(`MARKET_STUDIO_SETTINGS.${microAssetClass}`)})`}
						/>
					</Row>
				),
			},
		],
		[aliases, assetClassType, custom, onSelectionChange, rowsRef, t],
	);

	const [showRenameModal, setShowRenameModal] = useState(false);
	const [showAssetClassSelectionModal, setShowAssetClassSelectionModal] = useState(false);

	const instrumentsQuery = marketViewRepository.marketViewSettingsSelectableAssetClasses.useQuery(
		assetClassType,
		custom,
	);
	const microAssetClassSelectCtx = useMultiSelect();
	const microAssetClassSelectCtxRef = useUnsafeUpdatedRef(microAssetClassSelectCtx);
	useEffect(() => {
		microAssetClassSelectCtxRef.current.setSelection(rows.map((microAssetClass) => microAssetClass));
	}, [microAssetClassSelectCtxRef, rows]);

	return (
		<>
			<Searchable
				extractFn={(item) => `${t(`MARKET_STUDIO_SETTINGS.${item}` as any)} ${aliases.get(item) ?? ""}`}
				collection={rows}
			>
				{({ filtered, query, setQuery }) => (
					<div>
						<div className="flex justify-between items-end mb-4">
							<div>
								<FormField label="Market view asset classes">
									{(forward) => (
										<TextInput
											{...forward}
											className="w-[336px]"
											name="microAssetClass"
											onChangeText={setQuery}
											value={query}
											placeholder="Search"
											leftContent={<Icon icon="Search" size={20} color={themeCSSVars.palette_N300} />}
											data-qualifier={`MarketViewSettings/${
												custom ? "Custom" : qualifierMarketViewType[assetClassType]
											}/Search`}
										/>
									)}
								</FormField>
							</div>
							<div className="flex gap-2">
								<Button
									size="small"
									palette="secondary"
									onClick={() => setShowRenameModal(true)}
									data-qualifier={`MarketViewSettings/${
										custom ? "Custom" : qualifierMarketViewType[assetClassType]
									}/Action(Rename)`}
								>
									<Icon icon="Edit" size={16} />
									&nbsp;Rename
								</Button>
								<ReactQueryWrapperBase
									query={instrumentsQuery}
									loadingFallback={<CircularProgressBar value="indeterminate" outerDiameter={24} />}
								>
									{({ selectableAssetClasses }) => (
										<>
											<Button
												disabled={!selectableAssetClasses}
												size="small"
												palette="secondary"
												onClick={() => setShowAssetClassSelectionModal(true)}
												data-qualifier={`MarketViewSettings/${
													custom ? "Custom" : qualifierMarketViewType[assetClassType]
												}/Action(AddAssetClass)`}
											>
												<Icon icon="Outline1" size={16} />
												&nbsp;Add asset class
											</Button>

											{selectableAssetClasses && (
												<AddRemoveMicroAssetClassDialog
													rows={selectableAssetClasses}
													selected={rows.map((microAssetClass) => microAssetClass)}
													aliases={aliases}
													onClose={() => setShowAssetClassSelectionModal(false)}
													onSubmitAsync={(selection) => {
														onSelectionChange(selection);
														setShowAssetClassSelectionModal(false);
													}}
													show={showAssetClassSelectionModal}
												/>
											)}
										</>
									)}
								</ReactQueryWrapperBase>
							</div>
						</div>
						<AutoSortTable columns={assetClassAliasTableColumns} rows={filtered} style={{ maxHeight: 420 }} />
					</div>
				)}
			</Searchable>
			<RenameMicroAssetClassDialog
				aliases={aliases}
				show={showRenameModal}
				rows={rows}
				onClose={() => setShowRenameModal(false)}
				onSubmitAsync={(newAliases) => {
					onBatchRename(newAliases);
					setShowRenameModal(false);
				}}
			/>
		</>
	);
}

const AddRemoveMicroAssetClassDialog = (props: {
	show: boolean;
	onSubmitAsync(microAssetClasses: MarketViewAssetClassIdentifier[]): MaybePromise<void>;
	aliases: Map</* microAssetClass */ MarketViewAssetClassIdentifier, /* alias */ string>;
	onClose(): void;
	rows: Array<MarketViewAssetClassIdentifier>;
	selected: Array<MarketViewAssetClassIdentifier>;
}) => {
	const { t } = useTranslation();

	const {
		query: searchedMicroAssetClasses,
		setQuery: setSearchedMicroAssetClasses,
		filtered,
	} = useSearchable({
		collection: props.rows,
		extractFn: (microAssetClass) =>
			`${t(`MARKET_STUDIO_SETTINGS.${microAssetClass}` as any)} ${props.aliases.get(microAssetClass) ?? ""}`,
	});

	const checkboxColumnData = useSelectableTableColumn({
		rows: props.rows,
		filteredRows: filtered,
		selectBy: (microAssetClass): MarketViewAssetClassIdentifier => microAssetClass,
		mode: "checkbox",
	});

	const checkboxColumnDataMultiSelectCtxRef = useUnsafeUpdatedRef(checkboxColumnData.multiSelectCtx);
	useEffect(() => {
		checkboxColumnDataMultiSelectCtxRef.current.setSelection(props.selected);
	}, [checkboxColumnDataMultiSelectCtxRef, props.selected]);

	const columns = useMemo<Array<TableColumn<MarketViewAssetClassIdentifier>>>(
		() => [
			checkboxColumnData.column,
			microAssetClassColumns.microAssetClass(t),
			{
				name: "alias",
				minWidth: 220,
				header: t("TABLE.HEADERS.ALIAS"),
				content: (microAssetClass) => props.aliases.get(microAssetClass) ?? "",
			},
		],
		[checkboxColumnData.column, t, props.aliases],
	);

	return (
		<Dialog
			show={props.show}
			header="Select one of more instruments"
			size="xxlarge"
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton data-qualifier="MarketViewSettings/Dialog(AddAssetClass)/Action(Submit)">
							{t("BUTTON.DONE")}
						</SubmitButton>
					}
					neutralAction={
						<Button
							data-qualifier="MarketViewSettings/Dialog(AddAssetClass)/Action(Cancel)"
							palette="tertiary"
							onClick={props.onClose}
						>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
			onSubmitAsync={() => props.onSubmitAsync(checkboxColumnData.multiSelectCtx.selection.toArray())}
			onClose={props.onClose}
		>
			<TextInput
				leftContent={<Icon icon="Search" />}
				value={searchedMicroAssetClasses}
				onChangeText={setSearchedMicroAssetClasses}
				placeholder="Search your micro asset classes"
				className="mb-4"
				data-qualifier="MarketViewSettings/Dialog(AddAssetClass)/Search"
			/>

			<AutoSortTable
				style={{ maxHeight: 420 }}
				rows={filtered}
				columns={columns}
				onRowClick={(microAssetClass) => checkboxColumnData.toggle(microAssetClass)}
			/>
		</Dialog>
	);
};
